import React, { useState, useEffect } from "react";
import { X, Lock, User, Tag, AlignLeft, DollarSign, Edit, Trash, Plus, Upload } from "lucide-react";
import { formatCurrency } from "../utils/helpers";

const AdminPanel = ({ onClose }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProductData, setNewProductData] = useState({
    name: "",
    description: "",
    pricePerPound: "",
    image: "",
  });
  const [imageFile, setImageFile] = useState(null);

  // 🔄 cargar productos desde backend
  const fetchProducts = async () => {
    try {
      const res = await fetch("/backend/api/products.php");
      const data = await res.json();
      setProducts(data || []);
    } catch (error) {
      console.error("Error cargando productos:", error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchProducts();
  }, [isAuthenticated]);

  // 🔑 login real
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch("/backend/api/users.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setIsAuthenticated(true);
        setLoginError("");
      } else {
        setLoginError(data.error || "Usuario o contraseña incorrectos.");
      }
    } catch (err) {
      setLoginError("Error de conexión con el servidor.");
    }
  };

  const handleAddProductChange = (e) => {
    const { name, value } = e.target;
    setNewProductData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProductData((prev) => ({ ...prev, image: reader.result })); // base64
      };
      reader.readAsDataURL(file);
    } else {
      setImageFile(null);
      setNewProductData((prev) => ({ ...prev, image: "" }));
    }
  };

  const handleSaveProduct = async () => {
    if (!newProductData.name || !newProductData.pricePerPound) {
      alert("El nombre y el precio son obligatorios.");
      return;
    }

    try {
      const method = editingProduct ? "PUT" : "POST";
      const body = editingProduct
        ? { id: editingProduct.id, ...newProductData }
        : newProductData;

      const res = await fetch("/backend/api/products.php", {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (data.success) {
        alert("Producto guardado correctamente");
        setEditingProduct(null);
        setNewProductData({ name: "", description: "", pricePerPound: "", image: "" });
        setImageFile(null);
        fetchProducts();
      } else {
        alert("Error: " + (data.error || "No se pudo guardar"));
      }
    } catch (error) {
      alert("Error de conexión con el servidor");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      const res = await fetch(`/backend/api/products.php?id=${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        alert("Producto eliminado");
        fetchProducts();
      } else {
        alert("Error: " + (data.error || "No se pudo eliminar"));
      }
    } catch (error) {
      alert("Error de conexión con el servidor");
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setNewProductData({
      name: product.name,
      description: product.description,
      pricePerPound: product.pricePerPound,
      image: product.image || "",
    });
    setImageFile(null);
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setNewProductData({ name: "", description: "", pricePerPound: "", image: "" });
    setImageFile(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl h-[90vh] flex flex-col">
        <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3">
            <Lock className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
            Panel de Administración
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {!isAuthenticated ? (
          // 🔐 Login
          <div className="flex-grow flex items-center justify-center p-4 sm:p-6">
            <form onSubmit={handleLogin} className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-sm space-y-4 border">
              <h3 className="text-xl font-bold text-center text-gray-800 mb-4">Acceso de Administrador</h3>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Usuario" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-xl border" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="password" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-xl border" />
              </div>
              {loginError && <p className="text-red-600 text-sm text-center">{loginError}</p>}
              <button type="submit" className="w-full bg-purple-600 text-white py-2 rounded-xl">Ingresar</button>
            </form>
          </div>
        ) : (
          // 📦 CRUD de productos
          <div className="flex-grow p-4 sm:p-6 overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-800 mb-4">{editingProduct ? "Editar Producto" : "Agregar Nuevo Producto"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-gray-50 p-4 rounded-2xl border">
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" name="name" placeholder="Nombre" value={newProductData.name} onChange={handleAddProductChange} className="w-full pl-10 pr-4 py-2 rounded-xl border" />
              </div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="number" name="pricePerPound" placeholder="Precio por Libra" value={newProductData.pricePerPound} onChange={handleAddProductChange} step="0.01" className="w-full pl-10 pr-4 py-2 rounded-xl border" />
              </div>
              <div className="relative col-span-full">
                <AlignLeft className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <textarea name="description" placeholder="Descripción" value={newProductData.description} onChange={handleAddProductChange} rows="3" className="w-full pl-10 pr-4 py-2 rounded-xl border resize-none"></textarea>
              </div>
              <div className="col-span-full">
                <label className="block text-sm font-semibold mb-2">Foto del Producto</label>
                <label className="flex items-center gap-2 bg-gray-100 py-2 px-4 rounded-xl cursor-pointer hover:bg-gray-200">
                  <Upload className="w-5 h-5" />
                  Adjuntar Foto
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
                {newProductData.image && (
                  <div className="mt-4">
                    <img src={newProductData.image} alt="Preview" className="max-h-32 w-full object-cover rounded-lg border" />
                  </div>
                )}
              </div>
              <div className="col-span-full flex gap-3 mt-6">
                <button onClick={handleSaveProduct} className="flex-1 bg-green-500 text-white py-2 rounded-xl flex items-center justify-center gap-2 hover:bg-green-600">
                  <Plus className="w-5 h-5" />
                  {editingProduct ? "Guardar Cambios" : "Añadir Producto"}
                </button>
                {editingProduct && (
                  <button onClick={handleCancelEdit} className="flex-1 bg-gray-300 text-gray-800 py-2 rounded-xl">Cancelar</button>
                )}
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-800 mb-4">Productos Existentes</h3>
            <div className="space-y-3">
              {products.map((product) => (
                <div key={product.id} className="flex items-center bg-gray-50 p-3 rounded-xl border gap-3">
                  <img src={product.image || "https://via.placeholder.com/64x64?text=Img"} alt={product.name} className="w-16 h-16 object-cover rounded-lg" />
                  <div className="flex-grow">
                    <h4 className="font-semibold">{product.name}</h4>
                    <p className="text-sm">{formatCurrency(product.pricePerPound)}/lb</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEditClick(product)} className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteProduct(product.id)} className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"><Trash className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
